import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, createStandardMenuItems } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { Tooltip } from "@msuite/ui/tooltip";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import React, { memo, useMemo } from "react";
import type { CrewDefinitionListItem } from "@/hooks/use-crew-definitions";
import { client } from "@/hono";
import { AddManagerSlotModal } from "@/modals/plan/add-manager-slot-modal";
import { EditCrewDefinitionModal } from "@/modals/plan/edit-crew-definition-modal";
import {
	CapacityCalendarCells,
	CapacityCalendarRowWrapper,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import {
	type CrewBarSegment,
	buildCrewBarGradient,
	buildManagerCoverageGradient,
} from "./bar-gradient";
import { CapacityPlannerBar } from "./capacity-planner-bar";
import { CapacityPlannerManagerSlotRows } from "./capacity-planner-manager-slot-rows";
import { CapacityPlannerRowHead } from "./capacity-planner-row-head";

/** Props Interface */
interface CapacityPlannerCrewRowProps {
	crewDefinition: CrewDefinitionListItem;
	absenceDates: ReadonlySet<string>;
}

function CapacityPlannerCrewRowImpl({ crewDefinition, absenceDates }: CapacityPlannerCrewRowProps) {
	/** Context */
	const { controller, getIsExpanded, toggleExpanded } = useCapacityCalendarContext();

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const addManagerSlotModal = useDisclosure();
	const editModal = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.crew_definitions[":crewDefinitionId"].$delete({
			param: { crewDefinitionId: crewDefinition.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
	}

	// The foreman leads the line, because a yard names a crew by who runs it. The rest follow in
	// the order the read gave them.
	function getMemberNames(): string[] {
		const names: string[] = [];
		for (const member of crewDefinition._members) {
			if (!member.isLead) continue;
			if (member.user?.name) names.push(member.user.name);
		}
		for (const member of crewDefinition._members) {
			if (member.isLead) continue;
			if (member.user?.name) names.push(member.user.name);
		}
		return names;
	}

	/** Computed */
	// The row is drawn because the span overlaps the window, which is not the same as the span
	// covering every day of it — a crew starting mid-week has transparent days before it.
	const gradient = useMemo(() => {
		const segments: CrewBarSegment[] = controller.cells.map((cell) => {
			const date = cell.cellMoment.db();
			if (date < crewDefinition.startDate) return null;
			if (crewDefinition.endDate && date > crewDefinition.endDate) return null;
			return absenceDates.has(date) ? "absent" : "staffed";
		});
		return buildCrewBarGradient(segments);
	}, [controller.cells, crewDefinition.startDate, crewDefinition.endDate, absenceDates]);

	const isExpanded = getIsExpanded(crewDefinition.id);

	// Which of the crew's days some manager already holds — the question the strip answers, and the
	// only one it can: windows overlap, so it says covered or not and leaves whose window it is to
	// the rows beneath.
	const coverageGradient = useMemo(() => {
		const covered = controller.cells.map((cell) => {
			const date = cell.cellMoment.db();
			for (const managerSlot of crewDefinition._managerSlots) {
				if (date < managerSlot.startDate) continue;
				if (managerSlot.endDate && date > managerSlot.endDate) continue;
				return true;
			}
			return false;
		});
		return buildManagerCoverageGradient(covered);
	}, [controller.cells, crewDefinition._managerSlots]);

	// The whole crew on one line. It truncates rather than wrapping, because a row that grows with
	// its crew would put the day columns of two rows at different heights.
	// The crew's span, the way the manager slot card writes a window: an open end is an em dash to
	// infinity rather than an invented date. It leads the caption because it is what the bar across
	// from it draws — the names say who, the span says when, and a reader scanning the left column
	// for "who is around next month" needs the second without opening the row.
	const spanLabel = `${moment(crewDefinition.startDate).format("DD.MM.YYYY")} – ${
		crewDefinition.endDate ? moment(crewDefinition.endDate).format("DD.MM.YYYY") : "∞"
	}`;

	const memberNames = getMemberNames();
	const caption = memberNames.length
		? memberNames.join(", ")
		: t("planning.crews.crew_has_no_members");

	/** Render */
	return (
		<React.Fragment>
			<CapacityCalendarRowWrapper variant="content">
				<CapacityPlannerRowHead
					// The handle stays live whether or not the crew has windows: what is under it is
					// only known once it is opened, and the empty row underneath says so.
					handle={
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} />}
							onClick={() => toggleExpanded(crewDefinition.id)}
						/>
					}
					actions={
						<React.Fragment>
							<Button
								variant="white"
								size="xxs"
								icon={<Icon name="Plus" />}
								onClick={addManagerSlotModal.onOpen}
							/>
							<Menu
								items={createStandardMenuItems({
									onEdit: editModal.onOpen,
									onDelete: handleOnDelete,
								})}
							>
								<Button
									variant="white"
									size="xxs"
									icon={<Icon name="EllipsisVertical" />}
								/>
							</Menu>
						</React.Fragment>
					}
				>
					<p className="text-sm truncate font-semibold">
						{guardString(crewDefinition.displayName, t("planning.crews.unnamed_crew"))}
					</p>
					<Tooltip
						label={caption}
						isDisabled={memberNames.length < 2}
					>
						<p className="label truncate">{`${spanLabel} · ${caption}`}</p>
					</Tooltip>
				</CapacityPlannerRowHead>
				<CapacityCalendarCells />
				<CapacityPlannerBar
					gradient={gradient}
					underlayGradient={coverageGradient}
				/>
			</CapacityCalendarRowWrapper>
			{isExpanded && <CapacityPlannerManagerSlotRows crewDefinition={crewDefinition} />}
			{addManagerSlotModal.isOpen && (
				<AddManagerSlotModal
					isOpen={addManagerSlotModal.isOpen}
					onClose={addManagerSlotModal.onClose}
					crewDefinitionId={crewDefinition.id}
				/>
			)}
			{editModal.isOpen && (
				<EditCrewDefinitionModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					crewDefinitionId={crewDefinition.id}
				/>
			)}
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deletion")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}

export const CapacityPlannerCrewRow = memo(CapacityPlannerCrewRowImpl);
